import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { ColumnDef } from "@tanstack/react-table";
import DataTable from "@/components/controls/DataTable/DataTable";
import Text from "@/components/ui/text";
import { IconButton } from "@/components/ui/icon-button";
import { ExpandView, ReportBadge } from "@dreamhub/designsystem/icons";
import { isoToShortDate } from "@/utils/datetime";
import { REPORTS_TABLE_HEADER_CLASSNAME } from "@/components/global/Chat/components/dreamerReports/ReportsTableModal";
import { TruncatedTextCell } from "@/components/global/Chat/components/dreamerReports/reportTableCells";
import { useReportingSchedulersFilter } from "@/entities/reportingScheduler/hooks";
import { ReportingScheduler } from "@/entities/reportingScheduler/types";
import {
  RECURRING_REPORTS_TRANSLATION_KEY,
  TRANSLATION_KEY
} from "@/components/global/Chat/components/dreamerReports/consts";

const MAX_INLINE_ROWS = 5;

// Column definitions at module scope — stable references across renders, per
// the tanstack-table playbook rule (no inline column def inside the component).
function buildInlineScheduleColumns(
  t: TFunction,
  untitledLabel: string
): ColumnDef<ReportingScheduler, unknown>[] {
  return [
    {
      id: "name",
      accessorKey: "name",
      header: t(`${TRANSLATION_KEY}.nameHeader`),
      enableSorting: false,
      cell: ({ row }) => (
        <TruncatedTextCell variant="body">
          {row.original.name || untitledLabel}
        </TruncatedTextCell>
      )
    },
    {
      id: "nextRunAt",
      accessorKey: "nextRunAt",
      header: t(`${RECURRING_REPORTS_TRANSLATION_KEY}.nextDeliveryDate`),
      enableSorting: false,
      cell: ({ row }) => (
        <Text as="div">
          {row.original.nextRunAt
            ? isoToShortDate(row.original.nextRunAt)
            : t(`${RECURRING_REPORTS_TRANSLATION_KEY}.noNextRun`)}
        </Text>
      )
    }
  ];
}

interface InlineScheduledReportsProps {
  /** Called when the user clicks the Expand button — caller mounts the modal. */
  onExpand: () => void;
}

/**
 * Compact, read-only schedules table rendered inline in a Dreamer chat balloon
 * in response to a `reporting.scheduler.filter` MessageAction ("show me my
 * reports"). Shows report name + next delivery date, soonest first; the Expand
 * button is delegated upward so the caller controls the full RecurringReportsModal
 * (avoids double-mounting the modal when multiple chat balloons are rendered).
 */
const InlineScheduledReports: React.FC<InlineScheduledReportsProps> = ({
  onExpand
}) => {
  const { t } = useTranslation();
  const untitledLabel = t(`${TRANSLATION_KEY}.untitled`);
  const columns = useMemo(
    () => buildInlineScheduleColumns(t, untitledLabel),
    [t, untitledLabel]
  );

  // Live fetch via React Query (fresh on mount / focus / reconnect), sorted by
  // next run so the soonest delivery is first — same hook the modal uses.
  const {
    data: schedules = [],
    isLoading,
    isFetching
  } = useReportingSchedulersFilter(true, undefined, "nextRunAt", true);

  // Only show empty state when fully settled — avoids a flash during background
  // refetches where isFetching is true but isLoading is already false.
  const hasNoResults = !isLoading && !isFetching && schedules.length === 0;
  const visibleSchedules = schedules.slice(0, MAX_INLINE_ROWS);

  return (
    <div className="flex flex-col gap-2 py-1.5 px-4 items-start">
      <div className="w-full bg-surface-elevation2 border-[0.5px] border-border-widget-card rounded-2xl shadow-md flex flex-col overflow-hidden">
        <div className="flex w-full justify-between items-center gap-2 px-4 py-4">
          <Text as="div" variant="lgLabel">
            {t(`${RECURRING_REPORTS_TRANSLATION_KEY}.title`)}
          </Text>
          <IconButton
            size="lg"
            variant="iconNoBg"
            className="p-[5.5px]"
            onClick={onExpand}
            data-test="inline-scheduled-reports-expand"
            aria-label={t(
              `${RECURRING_REPORTS_TRANSLATION_KEY}.expandAriaLabel`
            )}
          >
            <ExpandView />
          </IconButton>
        </div>
        {hasNoResults ? (
          <div className="flex flex-col items-center justify-center gap-2 p-6">
            <ReportBadge />
            <Text variant="body">
              {t(`${RECURRING_REPORTS_TRANSLATION_KEY}.empty`)}
            </Text>
          </div>
        ) : (
          <DataTable
            dataTest="inline-scheduled-reports-table"
            columns={columns}
            data={visibleSchedules}
            isDataLoading={isLoading || isFetching}
            headerClassName={REPORTS_TABLE_HEADER_CLASSNAME}
            className="w-full max-h-[300px]"
          />
        )}
      </div>
    </div>
  );
};

export default InlineScheduledReports;
